import {Component, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {SettingsService} from 'src/app/services/settings.service';
import {DialogService} from 'src/app/services/dialog.service';
import {Observable, filter, switchMap} from 'rxjs';
import {AddingModalComponent} from './adding-modal/adding-modal.component';
import {PositionModalComponent} from './position-modal/position-modal.component';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {SureModalService} from 'src/app/services/sure-modal/sure-modal.service';
import {
  SURE_MODAL_BUTTONS,
  SURE_MODAL_TEXT,
} from 'src/app/shared/utils/constants';

@UntilDestroy()
@Component({
  selector: 'app-tech-stacks-and-positions',
  templateUrl: './tech-stacks-and-positions.component.html',
  styleUrls: ['./tech-stacks-and-positions.component.scss'],
})
export class TechStacksAndPositionsComponent implements OnInit {
  techStacks: IDropdownObject[] = [];
  positions: IDropdownObject[] = [];
  frameworks: IDropdownObject[] = [];

  private readonly SURE_BUTTONS = SURE_MODAL_BUTTONS;
  private readonly SURE_TEXT = SURE_MODAL_TEXT;

  constructor(
    private settingsService: SettingsService,
    private dialogService: DialogService,
    private sureModalService: SureModalService
  ) {}

  ngOnInit(): void {
    this.getTechStacks();
    this.getPositions();
    this.getFrameworks();
  }

  addPopup(title: string): Observable<string> {
    return this.dialogService
      .open(AddingModalComponent, {
        data: title,
        settings: {
          width: '400px',
          height: '230px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(value => !!value)
      );
  }

  deletePopup(name: string): Observable<boolean> {
    const {DELETE, CANCEL} = this.SURE_BUTTONS;
    return this.sureModalService.confirmAction({
      text: this.SURE_TEXT.DELETE_TEXT(name || ''),
      acceptLabel: DELETE,
      rejectLabel: CANCEL,
    });
  }

  getTechStacks(): void {
    this.settingsService
      .getTechStacks()
      .pipe(untilDestroyed(this))
      .subscribe(data => (this.techStacks = data));
  }

  addTechStack(): void {
    this.addPopup('Tech Stack')
      .pipe(switchMap(v => this.settingsService.addTechStack(v)))
      .subscribe(data => (this.techStacks = data));
  }

  deleteTechStack(techStack: IDropdownObject): void {
    this.deletePopup(techStack.name)
      .pipe(switchMap(() => this.settingsService.deleteTechStack(techStack.id)))
      .subscribe(data => (this.techStacks = data));
  }

  getPositions(): void {
    this.settingsService
      .getPositions()
      .pipe(untilDestroyed(this))
      .subscribe(data => (this.positions = data));
  }

  addPosition(): void {
    this.settingsService
      .getDepartmentDropdowns()
      .pipe(
        untilDestroyed(this),
        switchMap(data =>
          this.dialogService
            .open(PositionModalComponent, {
              data,
              settings: {
                width: '430px',
                height: '400px',
              },
            })
            .afterClosed()
        ),
        filter(value => !!value),
        switchMap(v => this.settingsService.addPosition(v))
      )
      .subscribe(data => (this.positions = data));
  }

  deletePosition(position: IDropdownObject): void {
    this.deletePopup(position.name)
      .pipe(switchMap(() => this.settingsService.deletePosition(position.id)))
      .subscribe(data => (this.positions = data));
  }

  getFrameworks(): void {
    this.settingsService
      .getFrameworks()
      .pipe(untilDestroyed(this))
      .subscribe(data => (this.frameworks = data));
  }

  addFramework(): void {
    this.addPopup('Framework')
      .pipe(switchMap(v => this.settingsService.addFramework(v)))
      .subscribe(data => (this.frameworks = data));
  }

  deleteFramework(framework: IDropdownObject): void {
    this.deletePopup(framework.name)
      .pipe(switchMap(() => this.settingsService.deleteFramework(framework.id)))
      .subscribe(data => (this.frameworks = data));
  }
}
